import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import ExoDialog from '../exo/ExoDialog';
import ExoForm from '../exo/ExoForm';
import * as Yup from 'yup';
import { DBUploadData, useConfig } from '../../api/api';
import moment from 'moment';

const pdfOptions = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
};

const MultiPagePayrollWizard = ({
  open,
  onClose,
  splitPages,
  onComplete,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedPayrolls, setCompletedPayrolls] = useState([]);
  const [lastMonth, setLastMonth] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfScale, setPdfScale] = useState(1);
  const [pdfContainerSize, setPdfContainerSize] = useState({ width: 0 });
  const pdfContainerRef = useRef(null);

  const zoomIn = () => setPdfScale((s) => Math.min(s + 0.25, 3));
  const zoomOut = () => setPdfScale((s) => Math.max(s - 0.25, 0.5));
  const resetZoom = () => setPdfScale(1);

  useEffect(() => {
    const node = pdfContainerRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setPdfContainerSize({ width });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const pdfFitWidth = useMemo(() => {
    const { width } = pdfContainerSize;
    if (width > 0) return Math.floor(width - 8);
    // fallback before ResizeObserver fires: viewport minus form panel and padding
    return Math.max(window.innerWidth - 550, 350);
  }, [pdfContainerSize]);

  const currentPage = splitPages[currentIndex];
  const totalPages = splitPages.length;

  const pdfUrl = useMemo(() => {
    if (currentPage?.blob) {
      return URL.createObjectURL(currentPage.blob);
    }
    return null;
  }, [currentPage]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setCompletedPayrolls([]);
      setLastMonth(null);
      setIsLoading(false);
    }
  }, [open]);

  const validationSchema = Yup.object().shape({
    incomeTax: Yup.string().required(t('Is required')),
    incomeBrutto: Yup.string().required(t('Is required')),
    incomeNetto: Yup.string().required(t('Is required')),
    month: Yup.string().required(t('Is required')),
    user: Yup.object().shape({
      id: Yup.string().required(t('Is required')),
    }),
  });

  const fields = [
    {
      label: `${t('Transactions')} (${t('optional')})`,
      type: 'transactionArray',
      key: 'transactions',
    },
    {
      label: t('Employee'),
      type: 'user',
      key: 'user',
    },
    {
      label: t('Month'),
      type: 'date',
      key: 'month',
      views: ['month'],
      format: 'MM/YYYY',
    },
    {
      label: t('Voluntary Social Benefits'),
      type: 'money',
      key: 'voluntarySocialBenefits',
    },
    {
      label: t('Income Tax'),
      type: 'money',
      key: 'incomeTax',
    },
    {
      label: t('Income Brutto'),
      type: 'money',
      key: 'incomeBrutto',
    },
    {
      label: t('Income Netto'),
      type: 'money',
      key: 'incomeNetto',
    },
    {
      label: `${t('Comment')} (${t('optional')})`,
      type: 'description',
      key: 'comment',
    },
  ];

  const startDataCollection = useMemo(() => {
    if (lastMonth) {
      return { month: lastMonth };
    }
    return {};
  }, [lastMonth]);

  const handleUpload = (rawData) => {
    const formData = new FormData();

    if (currentPage?.file) {
      formData.append('file', currentPage.file);
    }

    if (rawData.amount) formData.append('amount', rawData.amount);
    if (rawData.month) {
      const startDate = moment(rawData.month, 'MM/YYYY')
        .startOf('M')
        .format('YYYY-MM-DD');
      const endDate = moment(rawData.month, 'MM/YYYY')
        .endOf('M')
        .format('YYYY-MM-DD');

      formData.append('startDate', startDate);
      formData.append('endDate', endDate);
      setLastMonth(rawData.month);
    }
    if (rawData.user) formData.append('user', JSON.stringify(rawData.user));
    if (rawData.transactions)
      formData.append('transactions', JSON.stringify(rawData.transactions));

    if (rawData.voluntarySocialBenefits)
      formData.append('voluntarySocialBenefits', rawData.voluntarySocialBenefits);
    if (rawData.incomeBrutto) formData.append('incomeBrutto', rawData.incomeBrutto);
    if (rawData.incomeNetto) formData.append('incomeNetto', rawData.incomeNetto);
    if (rawData.incomeTax) formData.append('incomeTax', rawData.incomeTax);
    if (rawData.comment) formData.append('comment', rawData.comment);

    setIsLoading(true);
    DBUploadData({
      config,
      path: 'payrolls',
      formData,
      onResponse: (response) => {
        setIsLoading(false);
        const newCompleted = [...completedPayrolls, response];
        setCompletedPayrolls(newCompleted);

        if (currentIndex < totalPages - 1) {
          setCurrentIndex((prev) => prev + 1);
        } else {
          onComplete(newCompleted);
        }
      },
      onError: () => setIsLoading(false),
    });
  };

  const handleSkip = () => {
    if (currentIndex < totalPages - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      onComplete(completedPayrolls);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  if (!currentPage) return null;

  return (
    <ExoDialog open={open} onClose={onClose}>
      <Box
        className="flex flex-col h-full overflow-hidden rounded-lg"
        sx={{ bgcolor: colors.glass }}
      >
        <Box
          className="px-4 py-3 flex justify-between items-center"
          sx={{
            bgcolor: colors.card,
            borderBottom: `1px solid ${colors.selected}`,
          }}
        >
          <Box className="flex items-center gap-3">
            <Typography variant="h6">
              {t('Payroll {{current}} of {{total}}', {
                current: currentIndex + 1,
                total: totalPages,
              })}
            </Typography>
            <Box className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor:
                      i < currentIndex
                        ? colors.success
                        : i === currentIndex
                          ? colors.info
                          : colors.selected,
                    transition: 'all 0.2s',
                  }}
                />
              ))}
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: colors.text, opacity: 0.6 }}>
            {t('Page')} {currentPage.pageNumber}
          </Typography>
        </Box>

        <Box className="flex-1 overflow-auto flex flex-col lg:flex-row">
          <Box
            className="flex flex-col relative"
            sx={{
              bgcolor: colors.bgInput,
              minHeight: '250px',
              maxHeight: { xs: '40vh', lg: 'unset' },
              flex: { xs: 'none', lg: 1 },
              overflow: 'hidden',
            }}
          >
            <Box
              className="flex justify-center gap-1 py-1"
              sx={{
                bgcolor: colors.glass,
                borderBottom: `1px solid ${colors.selected}`,
                position: 'sticky',
                top: 0,
                zIndex: 2,
              }}
            >
              <IconButton size="small" onClick={zoomIn}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={resetZoom}>
                <FitScreenIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={zoomOut}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              ref={pdfContainerRef}
              className="flex-1 p-1"
              sx={{ overflow: 'auto' }}
            >
              {pdfUrl && (
                <Document file={pdfUrl} options={pdfOptions}>
                  <Page
                    pageNumber={1}
                    width={pdfFitWidth}
                    scale={pdfScale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="rounded overflow-hidden"
                    style={{
                      boxShadow: `0 2px 8px ${colors.backdrop}`,
                      margin: '0 auto',
                    }}
                  />
                </Document>
              )}
            </Box>
          </Box>

          <Box
            className="flex-1 p-4 overflow-auto"
            sx={{
              bgcolor: colors.paper,
              minWidth: { lg: '450px' },
              maxWidth: { lg: '500px' },
            }}
          >
            {isLoading ? (
              <Box className="flex items-center justify-center h-full">
                <CircularProgress sx={{ color: colors.info }} />
              </Box>
            ) : (
              <ExoForm
                key={currentIndex}
                validationSchema={validationSchema}
                startDataCollection={startDataCollection}
                noDifferences={true}
                fields={fields}
                onSubmit={handleUpload}
                header={null}
                onCancel={null}
                submitText={
                  currentIndex < totalPages - 1
                    ? t('Save and continue')
                    : t('Save and finish')
                }
                isNotValid={false}
              />
            )}
          </Box>
        </Box>

        <Box
          className="px-4 py-3 flex justify-between items-center"
          sx={{
            bgcolor: colors.card,
            borderTop: `1px solid ${colors.selected}`,
          }}
        >
          <Box>
            {currentIndex > 0 && (
              <Button
                variant="text"
                onClick={handleBack}
                disabled={isLoading}
                sx={{ color: colors.text }}
              >
                {t('Back')}
              </Button>
            )}
          </Box>
          <Button
            variant="text"
            onClick={handleSkip}
            disabled={isLoading}
            sx={{ color: colors.warning }}
          >
            {t('Skip')}
          </Button>
        </Box>
      </Box>
    </ExoDialog>
  );
};

export default MultiPagePayrollWizard;
